import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { User } from '../interfaces';
import { doc, getDoc } from 'firebase/firestore';
import { dataStore, db } from '../app.component';
import { Role } from '../enums';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PlanningComponent } from './tabs/planning/planning.component';
import { AvailabilityComponent } from './tabs/availability/availability.component';
import { ExceptionsComponent } from './tabs/exceptions/exceptions.component';
import { AvailabilityVehiclesComponent } from './tabs/availability-vehicles/availability-vehicles.component';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss'],
  imports: [
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatTabGroup,
    MatTab,
    MatProgressSpinner,
    PlanningComponent,
    AvailabilityComponent,
    ExceptionsComponent,
    AvailabilityVehiclesComponent,
  ],
})
export class ScheduleComponent implements OnInit, AfterViewInit, OnDestroy {
  organisationId = dataStore.selectedOrganisation.id;
  name = signal<string | null>(null);
  RoleName = signal<string | null>(null);
  generalSettings: any;
  userId = signal<string | null>(null);
  selectedType = signal<Role>(Role.Driver);
  currentUser = signal<User | null>(null);

  isDriver = signal(false);
  isCoDriver = signal(false);
  isAdmin = signal(false);
  isPlanner = signal(false);
  isRidePlanner = signal(false);
  usesPlannerPlanning = signal<boolean>(false);
  userCanModify = signal<boolean>(false);
  isLoading = signal(true);

  canSwitchType = computed(() => {
    const user = this.currentUser();
    if (!user) return false;
    const roles = user.roles ?? [];
    return (
      [
        roles.includes(Role.Driver),
        roles.includes(Role.CoDriver),
        roles.includes(Role.Planner) || roles.includes(Role.RidePlanner),
      ].filter(Boolean).length > 1
    );
  });

  returnTo = signal<'landing' | 'employees' | null>(null);

  @ViewChild('subHeader', { read: ElementRef })
  subHeaderRef?: ElementRef<HTMLElement>;

  @ViewChild('tabGroup', { read: ElementRef })
  tabGroupRef?: ElementRef<HTMLElement>;

  private resizeObserver?: ResizeObserver;

  RoleEnum = Role;
  dataStore = dataStore;

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
  ) {
    const navState = this.router.getCurrentNavigation()?.extras?.state;
    if (navState?.['from'] === 'landing' || navState?.['from'] === 'employees') {
      this.returnTo.set(navState['from']);
    }
  }

  async ngOnInit() {
    this.isLoading.set(true);
    try {
      this.redirectIfNotUsingDriverPlanning();
      let type = this.route.snapshot.paramMap.get('type');
      if (type) {
        type = type.replace('-', '_');
        this.selectedType.set(type as Role);
      }

      this.userId.set(this.route.snapshot.paramMap.get('userId'));
      if (this.userId() === 'unknown') {
        this.userId.set(null);
      }

      this.generalSettings = (
        await getDoc(doc(db, `organisations/${this.organisationId}/settings/general`))
      ).data();
      this.usesPlannerPlanning.set(!!this.generalSettings.usesPlannerPlanning);

      const ownUserDoc = await getDoc(
        doc(db, `organisations/${this.organisationId}/users/`, `${dataStore.rootUser.id}`),
      );
      const ownUser = { id: ownUserDoc.id, ...ownUserDoc.data() } as User;
      const ownRoles = ownUser.roles || [];

      if (ownRoles.includes(Role.Driver)) this.isDriver.set(true);
      if (ownRoles.includes(Role.CoDriver)) this.isCoDriver.set(true);
      if (ownRoles.includes(Role.Planner)) this.isPlanner.set(true);
      if (ownRoles.includes(Role.RidePlanner)) this.isRidePlanner.set(true);
      if (ownRoles.includes(Role.Administrator) || dataStore.rootUser.rights === 'admin') {
        this.isAdmin.set(true);
      }

      // Redirect RidePlanner (without planner/admin rights) back to own schedule
      if (
        this.userId() &&
        this.userId() !== dataStore.rootUser.id &&
        this.isRidePlanner() &&
        !this.isPlanner() &&
        !this.isAdmin()
      ) {
        this.userId.set(dataStore.rootUser.id!);
        this.updateStateLocation();
      }

      // Derive userId/type from own roles if not provided
      if (!this.userId()) {
        if (ownRoles.includes(Role.Driver)) {
          this.selectedType.set(Role.Driver);
        } else if (ownRoles.includes(Role.CoDriver)) {
          this.selectedType.set(Role.CoDriver);
        } else if (ownRoles.includes(Role.Planner) || ownRoles.includes(Role.RidePlanner)) {
          this.selectedType.set(Role.Planner);
        }
        this.userId.set(dataStore.rootUser.id!);
      }

      // Load the viewed user (own or another employee)
      let viewedUser: User;
      if (this.userId() === dataStore.rootUser.id) {
        viewedUser = ownUser;
      } else {
        const viewedDoc = await getDoc(
          doc(db, `organisations/${this.organisationId}/users/`, this.userId()!),
        );
        viewedUser = { id: viewedDoc.id, ...viewedDoc.data() } as User;
      }

      this.changeUser(viewedUser);
      this.setUserCanModify();

      // Set back button destination based on role and viewed user.
      // Router navigation state covers fresh navigations; this fallback covers page refreshes.
      if (!this.returnTo()) {
        const isElevated = this.isAdmin() || this.isPlanner();
        if (isElevated) {
          this.returnTo.set(this.userId() === dataStore.rootUser.id ? 'landing' : 'employees');
        }
      }
    } catch (error) {
      console.error('Failed to load schedule data', error);
    } finally {
      this.isLoading.set(false);
      this.syncTabHeightAfterRender();
    }
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateTabContentHeight();
    });

    this.syncTabHeightAfterRender();

    window.addEventListener('resize', this.updateTabContentHeight);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.updateTabContentHeight);
  }

  private syncTabHeightAfterRender() {
    queueMicrotask(() => {
      this.observeLayoutTargets();
      this.updateTabContentHeight();
    });

    requestAnimationFrame(() => {
      this.observeLayoutTargets();
      this.updateTabContentHeight();
    });
  }

  private observeLayoutTargets() {
    if (!this.resizeObserver) {
      return;
    }

    if (this.subHeaderRef?.nativeElement) {
      this.resizeObserver.observe(this.subHeaderRef.nativeElement);
    }

    if (this.tabGroupRef?.nativeElement) {
      this.resizeObserver.observe(this.tabGroupRef.nativeElement);
    }
  }

  private updateTabContentHeight = () => {
    const tabBodyWrapper = this.hostRef.nativeElement.querySelector(
      '.mat-mdc-tab-body-wrapper',
    ) as HTMLElement | null;

    if (!tabBodyWrapper) {
      return;
    }

    const bottomGap = 16;
    const contentChromeOffset = 64;

    const rect = tabBodyWrapper.getBoundingClientRect();
    const availableHeight = Math.max(
      220,
      Math.floor(window.innerHeight - rect.top - bottomGap - contentChromeOffset),
    );

    this.hostRef.nativeElement.style.setProperty(
      '--schedule-tab-content-height',
      `${availableHeight}px`,
    );
  };

  changeType(role: Role) {
    if (this.selectedType() !== role) {
      this.selectedType.set(role);
      this.setUserCanModify();
      this.updateStateLocation();
      this.updateRoleName();
      queueMicrotask(() => this.updateTabContentHeight());
    }
  }

  changeUser(user: any) {
    this.userId.set(user.id);
    this.currentUser.set(user);
    this.name.set(user.name);
    this.updateRoleName();
    this.updateStateLocation();
    queueMicrotask(() => this.updateTabContentHeight());
  }

  updateRoleName() {
    switch (this.selectedType()) {
      case Role.Driver:
        this.RoleName.set('Chauffeur');
        break;
      case Role.CoDriver:
        this.RoleName.set('Bijrijder');
        break;
      case Role.Planner:
        const roles = this.currentUser()?.roles || [];
        if (roles.includes(Role.RidePlanner) && !roles.includes(Role.Planner)) {
          this.RoleName.set('Ritplanner');
        } else {
          this.RoleName.set('Planner');
        }
        break;
      default:
        this.RoleName.set('Medewerker');
        break;
    }
  }

  setUserCanModify() {
    switch (true) {
      case this.selectedType() === Role.Driver &&
        dataStore.generalSettings.driversModifyAvailability === true:
      case this.selectedType() === Role.CoDriver &&
        dataStore.generalSettings.coDriversModifyAvailability === true:
      case this.selectedType() === Role.Planner &&
        dataStore.generalSettings.plannersModifyAvailability === true:
      case this.selectedType() !== Role.Planner && this.isPlanner():
      case dataStore.orgUser.roles?.includes(Role.Administrator):
      case dataStore.rootUser.rights === 'admin':
        this.userCanModify.set(true);
        break;
      default:
        this.userCanModify.set(false);
        break;
    }
  }

  updateStateLocation() {
    const type = this.selectedType().replace('_', '-');
    this.location.replaceState(`/schedule/${type}/${this.userId()}`);
  }

  goBack() {
    if (this.returnTo() === 'employees') {
      this.router.navigate(['/schedule/employees']);
    } else {
      this.router.navigate(['/schedule']);
    }
  }

  redirectIfNotUsingDriverPlanning() {
    const usesDriverPlanning = JSON.parse(localStorage.getItem('usesDriverPlanning')!);
    if (!usesDriverPlanning) {
      this.router.navigateByUrl('/plannen');
    }
  }
}
